import FotoVideographieHero from './components/FotoVideographieHero';
import FotoVideographieServices from './components/FotoVideographieServices';
import FotoVideographiePortfolio from './components/FotoVideographiePortfolio';
import FotoVideographieProcess from './components/FotoVideographieProcess';
import FotoVideographieFAQ from './components/FotoVideographieFAQ';
import FotoVideographieCTA from './components/FotoVideographieCTA';
import { useSEO } from '../../hooks/useSEO';

export default function FotoVideographiePage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://bamme-media.de';

  useSEO({
    title: 'Fotografie & Videografie Wernigerode | Bamme-Media',
    description: 'Professionelle Fotografie & Videografie in Wernigerode von Bamme-Media. Produktfotografie, Unternehmensfotos, Imagevideos und Content Creation für Ihr Business. Hochwertige Ergebnisse garantiert!',
    keywords: 'Fotografie Wernigerode, Videografie Wernigerode, Produktfotografie, Unternehmensfotos, Imagevideos, Content Creation, Fotostudio, Videoproduktion, Bamme-Media',
    canonical: `${siteUrl}/foto-videographie`,
    robots: 'index, follow',
    ogTitle: 'Fotografie & Videografie Wernigerode | Produktfotos, Imagevideos | Bamme-Media',
    ogDescription: 'Professionelle Fotografie & Videografie in Wernigerode von Bamme-Media. Produktfotografie, Unternehmensfotos, Imagevideos und Content Creation für Ihr Business.',
    ogImage: 'https://static.readdy.ai/image/8fd098698619b27c03cb78789aa098a8/fa291c43a7c1ed26d5ebb74eb50592e0.png',
    ogImageWidth: '1200',
    ogImageHeight: '630',
    ogImageAlt: 'Bamme-Media Fotografie & Videografie Services in Wernigerode',
    twitterTitle: 'Fotografie & Videografie Wernigerode | Produktfotos, Imagevideos | Bamme-Media',
    twitterDescription: 'Professionelle Fotografie & Videografie in Wernigerode von Bamme-Media. Produktfotografie, Unternehmensfotos, Imagevideos und Content Creation für Ihr Business.',
    twitterImage: 'https://static.readdy.ai/image/8fd098698619b27c03cb78789aa098a8/fa291c43a7c1ed26d5ebb74eb50592e0.png',
    twitterImageAlt: 'Bamme-Media Fotografie & Videografie Services in Wernigerode',
    geoPosition: '51.8312,10.7865',
    geoRegion: 'DE-ST',
    geoPlacename: 'Wernigerode',
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Service",
          "name": "Fotografie & Videografie",
          "description": "Professionelle Fotografie & Videografie in Wernigerode von Bamme-Media. Produktfotografie, Unternehmensfotos, Imagevideos und Content Creation für Ihr Business.",
          "provider": {
            "@type": "Organization",
            "name": "Bamme-Media",
            "url": siteUrl,
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Wernigerode",
              "postalCode": "38855",
              "addressRegion": "Sachsen-Anhalt",
              "addressCountry": "DE"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": "51.8312",
              "longitude": "10.7865"
            }
          },
          "areaServed": [
            {
              "@type": "Place",
              "name": "Wernigerode"
            },
            {
              "@type": "Place",
              "name": "Harz"
            },
            {
              "@type": "Place",
              "name": "Sachsen-Anhalt"
            }
          ],
          "serviceType": "Photography and Videography",
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Foto & Video Services",
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Produktfotografie",
                  "description": "Professionelle Produktfotos für E-Commerce, Kataloge und Marketing-Materialien"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Unternehmensfotos",
                  "description": "Business-Fotografie für Unternehmen, Teams und Corporate Events"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Imagevideos",
                  "description": "Professionelle Videoproduktion für Unternehmen und Marken"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Content Creation",
                  "description": "Visuelle Inhalte für Social Media und Online-Marketing"
                }
              }
            ]
          }
        },
        {
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "Wie lange dauert eine typische Foto- oder Videoproduktion?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Die Dauer hängt vom Umfang des Projekts ab. Ein einfaches Fotoshooting dauert 2-4 Stunden, während komplexere Videoproduktionen 1-3 Tage in Anspruch nehmen können. Wir besprechen den Zeitrahmen im Vorfeld ausführlich mit Ihnen."
              }
            },
            {
              "@type": "Question",
              "name": "Was kostet ein professionelles Imagevideo?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Die Kosten für ein Imagevideo variieren je nach Länge, Komplexität und Produktionsaufwand. Ein einfaches Imagevideo startet bei ca. 1.500€, während umfangreichere Produktionen mit Drohnenaufnahmen, mehreren Drehtagen und aufwendiger Postproduktion entsprechend mehr kosten. Wir erstellen Ihnen gerne ein individuelles Angebot."
              }
            },
            {
              "@type": "Question",
              "name": "Wo finden die Foto- und Videoshootings statt?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Wir kommen gerne zu Ihnen vor Ort – ob in Ihrem Unternehmen, bei Events oder an besonderen Locations in Wernigerode und Umgebung. Alternativ können wir auch in unserem Studio arbeiten oder gemeinsam passende Locations auswählen."
              }
            },
            {
              "@type": "Question",
              "name": "Wie lange dauert es bis zur Lieferung der fertigen Bilder und Videos?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Fotos erhalten Sie in der Regel innerhalb von 5-7 Werktagen nach dem Shooting. Bei Videos beträgt die Bearbeitungszeit je nach Umfang 2-3 Wochen. Für eilige Projekte bieten wir gegen Aufpreis auch Express-Bearbeitung an."
              }
            },
            {
              "@type": "Question",
              "name": "In welchen Formaten werden die Dateien geliefert?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Fotos liefern wir in hochauflösenden JPEGs, auf Wunsch auch als RAW-Dateien. Videos erstellen wir in Full HD oder 4K in gängigen Formaten wie MP4 oder MOV. Alle Formate werden für Ihren spezifischen Verwendungszweck optimiert – ob für Web, Social Media oder Print."
              }
            },
            {
              "@type": "Question",
              "name": "Bietet ihr auch Drohnenaufnahmen an?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Ja, wir verfügen über professionelle Drohnen und alle erforderlichen Genehmigungen für Luftaufnahmen in Wernigerode und der Harz-Region. Drohnenaufnahmen eignen sich perfekt für Immobilien, Events, Landschaften oder Unternehmensvideos und verleihen Ihren Inhalten eine besondere Perspektive."
              }
            },
            {
              "@type": "Question",
              "name": "Kann ich die Bilder und Videos kommerziell nutzen?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Ja, Sie erhalten die vollständigen Nutzungsrechte für alle gelieferten Bilder und Videos. Sie können diese ohne Einschränkungen für Ihre Website, Social Media, Werbematerialien, Kataloge und alle anderen kommerziellen Zwecke verwenden."
              }
            },
            {
              "@type": "Question",
              "name": "Welche Ausrüstung verwendet ihr?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Wir arbeiten mit professioneller Kameraausrüstung von Canon und Sony, hochwertigen Objektiven, Stabilisatoren, Drohnen und professionellem Licht- und Tonequipment. Unsere Ausrüstung wird regelmäßig aktualisiert, um höchste Qualität zu gewährleisten."
              }
            }
          ]
        }
      ]
    }
  });

  return (
    <div className="min-h-screen">
      <FotoVideographieHero />
      <FotoVideographieServices />
      <FotoVideographiePortfolio />
      <FotoVideographieProcess />
      <FotoVideographieFAQ />
      <FotoVideographieCTA />
    </div>
  );
}